/**
 * Fluent builder for every bio-facing URL — the single source of truth for bio
 * path templates AND for how a link's shape is derived from the serving context.
 *
 * `bioUrl` takes the page's {@link BioContext} (the contextual facts: url code +
 * whether we're on an independent custom domain) and decides the prefix per route
 * kind. Nothing outside this builder re-derives a prefix from a raw string, so a
 * future context flag (e.g. `bot`) changes every link here in one place.
 *
 * URLs are assembled at RENDER, never baked into cached view models: a cached
 * `/<urlCode>/…` link would go stale (and point at the wrong creator) after a
 * urlCode change, and would carry the wrong prefix on an independent domain.
 * Cached data carries only the primitives (slug / id / storeId); the page passes
 * its live `BioContext` ({@link resolveBioContext}) here to build the link.
 *
 * Two prefixes are derived from the context:
 *  - the ORDINARY prefix — `/<urlCode>` on the default/alias domain, `''` (clean)
 *    on an independent domain (the domain root IS the bio home).
 *  - the INTERCEPT prefix — `/<urlCode>` on default/alias, the `~` soft-nav
 *    sentinel on an independent domain, so a two-segment `/~/…` stays
 *    client-soft-navigable and the `@modal/(.)` intercept fires (proxy rewrites
 *    `/~/…` → `/~<urlCode>/…` for the render).
 *
 * Routes WITH an intercepting `@modal/(.)` counterpart take a {@link UrlOpt}:
 *  - `'auto'` (default, omit it) — derive the shape from the context: the
 *    intercept prefix, so the modal fires on soft-nav.
 *  - `'non-intercepting'` — force the ORDINARY prefix, i.e. the clean prefix-free
 *    hard-nav form (shareable permalinks, new-tab opens, crawlable links).
 *
 * @example
 *   bioUrl(ctx).home()                                       // /<urlCode>  |  /
 *   bioUrl(ctx).posts(2)                                     // …/posts?page=2
 *   bioUrl(ctx).collection(id)                               // intercept: …/collection/<id>  |  /~/collection/<id>
 *   bioUrl(ctx).collection(id, 'non-intercepting')           // clean:     …/collection/<id>  |  /collection/<id>
 *   bioUrl(ctx).series(slug, v => v.video(videoId))          // …/series/<slug>/videos/<videoId>
 *   bioUrl(ctx).series(slug, undefined, 'non-intercepting')  // clean series detail
 *   bioUrl(ctx).course(slug, c =>
 *     c.chapter(chapterSlug, v => v.video(videoId)))         // …/chapters/<chapterSlug>/videos/<videoId>
 */
import { config } from '@ps/config';
import type { PopStoreExtendedSubscriptionLevel } from '@ps/models/lib/creator-subscription';

import { type BioContext, INDEPENDENT_URL_CODE_PREFIX } from './bioContext';

/**
 * How to shape a link for a route that HAS an intercepting `@modal/(.)`
 * counterpart. `'auto'` derives the shape from the context (intercept prefix, so
 * the modal fires); `'non-intercepting'` forces the clean, prefix-free form.
 */
export type UrlOpt = 'auto' | 'non-intercepting';

/** Selects a specific video under a series / course / chapter base path. */
export interface VideoScope {
  video(videoId: string): string;
}

/** A course scope — its own videos plus its chapters (their page or their videos). */
export interface CourseScope extends VideoScope {
  /** A chapter's detail page, or (with `build`) a video under it. */
  chapter(chapterSlug: string): string;
  chapter(chapterSlug: string, build: (chapter: VideoScope) => string): string;
}

/** A bio content tab addressable by its own sub-route. */
export type BioTabKind = 'life' | 'store' | 'feed';

/** A real-estate listing scope — the listing page plus its gated resources. */
export interface RealEstateListingScope {
  /** A gated resource under the listing — `…/realestate/listings/<slug>/resource/<resourceId>`. */
  resource(resourceId: string): string;
}

export interface BioUrlBuilder {
  /** The public bio home — `/<urlCode>` (or `/` on an independent domain). */
  home(): string;
  /** A content tab — `/<urlCode>/<kind>` (the crawlable pagination base). */
  tab(kind: BioTabKind): string;
  /** The "My Life" content tab — `/<urlCode>/life`. */
  life(): string;
  /** The "My Store" content tab — `/<urlCode>/store`. */
  store(): string;
  /** The "My Feed" content tab — `/<urlCode>/feed`. */
  feed(): string;
  /**
   * The paginated posts listing — `/<urlCode>/posts` (page 1 / omitted is the
   * query-free canonical URL), else `/<urlCode>/posts?page=<page>`.
   */
  posts(page?: number): string;
  /** The community home (channel-list landing) — `/<urlCode>/community`, an ORDINARY route. */
  community(): string;
  /**
   * The community channels base (`/<urlCode>/community/channels`), or a specific
   * channel (`…/community/channels/<slug>`) — ORDINARY routes. The bare base is
   * the prefix channel/thread deep links are appended to; the community LANDING is
   * {@link BioUrlBuilder.community}.
   */
  communityChannels(channelSlug?: string): string;
  /**
   * A real-estate listing page — an ORDINARY route (`…/realestate/listings/<slug>`),
   * or (with `build`) a gated resource under it. See {@link RealEstateListingScope}.
   */
  realEstateListing(slug: string): string;
  realEstateListing(
    slug: string,
    build: (listing: RealEstateListingScope) => string,
  ): string;
  /** A live-show room — an ORDINARY route (`…/live/<showId>`). */
  live(showId: string): string;
  /** A live-show greenroom preview — an ORDINARY route (`…/live-preview/<showId>`). */
  livePreview(showId: string): string;
  /**
   * A product detail link, in the preferred form. With a `slug` it's the
   * creator-scoped slug route (`<prefix>/product/<slug>`, an intercepting
   * `@modal/(.)product`); without one it falls back to the legacy
   * `/products/<creatorId>/<id>` route (a root route, built from the context's
   * `creatorId`). `opt` shapes only the slug branch (default `'non-intercepting'`
   * — a PDP link is predominantly a canonical / new-tab / shareable target).
   */
  product(listing: { id: string; slug?: string | null }, opt?: UrlOpt): string;
  /** A collection detail page — intercepting `@modal/(.)collection`. */
  collection(collectionId: string, opt?: UrlOpt): string;
  /**
   * The All-Products view (`<prefix>/all-my-products`) — every storefront product in one
   * grid, intercepting `@modal/(.)all-my-products`. The "View All" target of the My Store
   * tab's "My Products" card, and the crawlable `?page=N` base its grid pages
   * against.
   */
  allProducts(opt?: UrlOpt): string;
  /**
   * The All-Videos view (`<prefix>/all-my-videos`) — every clip the creator published in
   * one grid. An ORDINARY route (no intercept: the swipe player, not this grid, is
   * what a clip tap opens), and the crawlable `?page=N` base the My Life
   * all-videos grid pages against.
   */
  allVideos(): string;
  /**
   * A post permalink from its url-code-FREE path (`post/<year>/<month>/<slug>`) —
   * intercepting `@modal/(.)post`. Cached data stores only the path; the live
   * prefix is prepended here at render.
   */
  postPermalink(path: string, opt?: UrlOpt): string;
  /** A series detail page, or (with `build`) a video under it — intercepting. */
  series(slug: string, opt?: UrlOpt): string;
  series(
    slug: string,
    build: (video: VideoScope) => string,
    opt?: UrlOpt,
  ): string;
  /** A course detail page, or (with `build`) a video / chapter-video — intercepting. */
  course(slug: string, opt?: UrlOpt): string;
  course(
    slug: string,
    build: (course: CourseScope) => string,
    opt?: UrlOpt,
  ): string;
  /**
   * A standalone ("loose") clip player — `<prefix>/videos/<clipId>` — for a
   * PopClip under no series/course (the My Life All-Videos grid target).
   * Intercepting `@modal/(.)videos`.
   */
  video(clipId: string, opt?: UrlOpt): string;
  /**
   * The bare prefix the loose swipe player hangs `/videos/<clipId>` off (its
   * `basePath` / share base) — the analogue of `series(slug)` for clips that
   * live directly under the bio root. Intercept prefix by default.
   */
  videoBase(opt?: UrlOpt): string;
  /**
   * The tipping surface — intercepting `@modal/(.)tipping`. Optional tip context
   * is carried as query params matching the checkout contract (`tip_type` /
   * `tip_goal` / `tip_ref` / `tip_amount`) so the surface forwards them straight
   * to `/checkout` on confirm.
   */
  tipping(params?: TippingUrlParams, opt?: UrlOpt): string;
  /** The contact-card surface — intercepting `@modal/(.)contact`. */
  contact(opt?: UrlOpt): string;
  /**
   * The subscribe surface — intercepting `@subscribe/(.)subscribe`. Optional
   * picker context is carried as query params the subscribe page reads
   * ({@link SubscribeUrlParams}: `defaultLevel` / `minLevel` / `redirect_to`), so
   * callers never hand-assemble the query string.
   */
  subscribe(params?: SubscribeUrlParams, opt?: UrlOpt): string;
  /** The community chat surface — intercepting `@modal/(.)chat`. */
  chat(opt?: UrlOpt): string;
  /** The full-screen welcome-video player — intercepting `@welcome-video/(.)welcome-video`. */
  welcomeVideo(opt?: UrlOpt): string;
}

/**
 * Optional picker context for {@link BioUrlBuilder.subscribe} — the full set of
 * optional query params the `/<urlCode>/subscribe` page reads. Every subscribe
 * link that needs a tier / return target passes these here instead of appending
 * its own query string.
 */
export interface SubscribeUrlParams {
  /** Preselect this tier when the picker opens (`?defaultLevel`). */
  defaultLevel?: PopStoreExtendedSubscriptionLevel;
  /** Floor — tiers below this render disabled (`?minLevel`). */
  minLevel?: PopStoreExtendedSubscriptionLevel;
  /** Post-subscribe return target, carried across the login round-trip (`?redirect_to`). */
  redirectTo?: string;
}

/** Optional tip context for {@link BioUrlBuilder.tipping}. */
export interface TippingUrlParams {
  /** The tip recipient context. Defaults to a direct creator tip. */
  type?: 'creator' | 'post' | 'live';
  /** A specific tipping goal id (`tip_goal`). */
  goalId?: string;
  /** The post / livestream id for post / live tips (`tip_ref`). */
  ref?: string;
  /** A pre-filled amount (`tip_amount`). */
  amount?: number;
}

function videoScope(base: string): VideoScope {
  return {
    video: videoId => `${base}/videos/${encodeURIComponent(videoId)}`,
  };
}

/**
 * Absolute origin for a `{ share: true }` link — the host the CURRENT request is
 * actually served on, NEVER a host the visitor isn't browsing:
 *  - the INDEPENDENT custom domain ({@link BioContext.domain}) → its own host,
 *    but ONLY when the request is served on it ({@link BioContext.isIndependent},
 *    derived from the route → deterministic + SSR-safe).
 *  - else an ALTERNATIVE pop-store alias ({@link BioContext.domainAlias}) → the
 *    alias, but ONLY when the visitor is currently on it (validated against the
 *    known alias so we never reflect an arbitrary host). Client-only, so SSR and
 *    other hosts fall through.
 *  - else the default pop-store host (`config.url`).
 * A creator merely HAVING a custom domain is not enough: a visitor on the
 * default/preview host must get a default-host link, not the custom domain.
 */
function shareOrigin(ctx: BioContext): string {
  if (ctx.isIndependent && ctx.domain) {
    return `https://${ctx.domain}`;
  }
  if (
    ctx.domainAlias != null &&
    typeof window !== 'undefined' &&
    window.location.origin === `https://${ctx.domainAlias}`
  ) {
    return `https://${ctx.domainAlias}`;
  }
  // The default host. Strip a trailing slash so we never emit `//<urlCode>`: on a
  // preview channel `config.url` is just `/` (the public URL's hash is unknown at
  // build time — see validateOrigin.ts), which would otherwise concatenate to a
  // malformed `//<urlCode>`. Prod (`https://…`, no trailing slash) is unchanged;
  // preview yields a clean root-relative link.
  return config.url.replace(/\/+$/, '');
}

/**
 * Root builder for URLs under a creator's bio, derived from the page's
 * {@link BioContext} — the single facility for every in-bio URL, RELATIVE
 * (`<Link>`/`<a>`) by default and ABSOLUTE with `{ share: true }` (copy/paste/
 * social links). Both the share origin and the prefix shape follow the CURRENT
 * serving host (see {@link shareOrigin} + {@link BioContext.isIndependent}), so a
 * share link on the default host stays on the default host even when the creator
 * owns a custom domain.
 */
export function bioUrl(
  ctx: BioContext,
  opts?: { share?: boolean },
): BioUrlBuilder {
  const share = opts?.share === true;
  // A share link is ABSOLUTE (copy/paste/social), rooted at the creator's canonical
  // host (custom domain when set, else the default pop-store host) — from the
  // context, so it's SSR-safe. Nav links are relative (no origin).
  const abs = share ? shareOrigin(ctx) : '';
  // Prefix shape follows the CURRENT serving mode for BOTH nav and share
  // ({@link BioContext.isIndependent}): prefix-free on an independent domain (its
  // root IS the bio home), `/<urlCode>` on the default/alias host. A share link
  // on the default host must stay prefixed + rooted at the default host — a
  // creator merely owning a custom domain does NOT make its links prefix-free.
  const independent = ctx.isIndependent;
  const ordinary = `${abs}${independent ? '' : `/${ctx.urlCode}`}`;
  const intercept = `${abs}${independent ? `/${INDEPENDENT_URL_CODE_PREFIX}` : `/${ctx.urlCode}`}`;
  // `{ share: true }` builds shareable/crawlable links — always the ordinary
  // permalink (the `~` soft-nav sentinel is meaningless outside the app), i.e.
  // `'auto'` behaves as `'non-intercepting'`.
  const prefix = (opt: UrlOpt) =>
    share || opt === 'non-intercepting' ? ordinary : intercept;

  return {
    home: () => ordinary || '/',
    tab: kind => `${ordinary}/${kind}`,
    life: () => `${ordinary}/life`,
    store: () => `${ordinary}/store`,
    feed: () => `${ordinary}/feed`,
    posts: page =>
      page != null && page > 1
        ? `${ordinary}/posts?page=${page}`
        : `${ordinary}/posts`,
    community: () => `${ordinary}/community`,
    communityChannels: channelSlug =>
      channelSlug
        ? `${ordinary}/community/channels/${channelSlug}`
        : `${ordinary}/community/channels`,
    realEstateListing: (
      slug: string,
      build?: (listing: RealEstateListingScope) => string,
    ) => {
      const base = `${ordinary}/realestate/listings/${slug}`;
      return build
        ? build({
            resource: resourceId =>
              `${base}/resource/${encodeURIComponent(resourceId)}`,
          })
        : base;
    },
    live: showId => `${ordinary}/live/${showId}`,
    livePreview: showId => `${ordinary}/live-preview/${showId}`,
    product: (listing, opt = 'non-intercepting') =>
      listing.slug
        ? `${prefix(opt)}/product/${listing.slug}`
        : `/products/${ctx.creatorId}/${listing.id}`,
    collection: (collectionId, opt = 'auto') =>
      `${prefix(opt)}/collection/${collectionId}`,
    allProducts: (opt = 'auto') => `${prefix(opt)}/all-my-products`,
    allVideos: () => `${ordinary}/all-my-videos`,
    postPermalink: (path, opt = 'auto') => `${prefix(opt)}/${path}`,
    series: (
      slug: string,
      buildOrOpt?: ((video: VideoScope) => string) | UrlOpt,
      maybeOpt?: UrlOpt,
    ) => {
      const build = typeof buildOrOpt === 'function' ? buildOrOpt : undefined;
      const opt =
        typeof buildOrOpt === 'string' ? buildOrOpt : (maybeOpt ?? 'auto');
      const base = `${prefix(opt)}/series/${slug}`;
      return build ? build(videoScope(base)) : base;
    },
    course: (
      slug: string,
      buildOrOpt?: ((course: CourseScope) => string) | UrlOpt,
      maybeOpt?: UrlOpt,
    ) => {
      const build = typeof buildOrOpt === 'function' ? buildOrOpt : undefined;
      const opt =
        typeof buildOrOpt === 'string' ? buildOrOpt : (maybeOpt ?? 'auto');
      const base = `${prefix(opt)}/course/${slug}`;
      if (!build) {
        return base;
      }
      return build({
        ...videoScope(base),
        chapter: (
          chapterSlug: string,
          chapterBuild?: (chapter: VideoScope) => string,
        ) => {
          const chapterBase = `${base}/chapters/${chapterSlug}`;
          return chapterBuild
            ? chapterBuild(videoScope(chapterBase))
            : chapterBase;
        },
      });
    },
    video: (clipId, opt = 'auto') =>
      `${prefix(opt)}/videos/${encodeURIComponent(clipId)}`,
    videoBase: (opt = 'auto') => prefix(opt),
    tipping: (params, opt = 'auto') => {
      const base = `${prefix(opt)}/tipping`;
      if (!params) {
        return base;
      }
      const search = new URLSearchParams();
      if (params.type) {
        search.set('tip_type', params.type);
      }
      if (params.goalId) {
        search.set('tip_goal', params.goalId);
      }
      if (params.ref) {
        search.set('tip_ref', params.ref);
      }
      if (params.amount != null) {
        search.set('tip_amount', String(params.amount));
      }
      const query = search.toString();
      return query ? `${base}?${query}` : base;
    },
    contact: (opt = 'auto') => `${prefix(opt)}/contact`,
    subscribe: (params, opt = 'auto') => {
      const base = `${prefix(opt)}/subscribe`;
      if (!params) {
        return base;
      }
      const search = new URLSearchParams();
      if (params.defaultLevel) {
        search.set('defaultLevel', params.defaultLevel);
      }
      if (params.minLevel) {
        search.set('minLevel', params.minLevel);
      }
      if (params.redirectTo) {
        search.set('redirect_to', params.redirectTo);
      }
      const query = search.toString();
      return query ? `${base}?${query}` : base;
    },
    chat: (opt = 'auto') => `${prefix(opt)}/chat`,
    welcomeVideo: (opt = 'auto') => `${prefix(opt)}/welcome-video`,
  };
}
